import { legalMoves, trickWinnerSeat, seatTeam } from "./setbackRules";

const lowestOf = (cards) =>
  cards.reduce((min, c) => (c.details.value < min.details.value ? c : min), cards[0]);

// Would `candidate` beat the current best card of the trick if played next?
const beats = (candidate, currentBest, trickSuit) => {
  const candTrump = candidate.details.trump;
  const bestTrump = currentBest.details.trump;
  if (candTrump && !bestTrump) return true;
  if (!candTrump && bestTrump) return false;
  if (candTrump && bestTrump) return candidate.details.value > currentBest.details.value;
  if (candidate.suit !== trickSuit) return false;
  if (currentBest.suit !== trickSuit) return true;
  return candidate.details.value > currentBest.details.value;
};

// Is this trick actually worth spending trump to win - does it carry any
// game points, or the trump jack itself (a scoring card on its own)?
const trickWorthTrumping = (trick) => {
  const points = trick.reduce((sum, c) => sum + (c.details.gamePoints || 0), 0);
  const hasJack = trick.some((c) => c.details.trump && c.details.value === 11);
  return points > 0 || hasJack;
};

// Simple, fast trick-taking heuristic used for MCTS rollouts and as the
// default policy for every seat during bid evaluation:
// - Leading: play the trump ace if held - it's the single highest-value
//   card in the deck, so it can never lose, and playing it guarantees
//   the High point immediately at zero risk. Otherwise play your lowest
//   card (keeps strong cards in hand for later).
// - Following, partner currently winning: play your lowest legal card.
// - Following, opponent currently winning: overtake with the cheapest
//   legal card that does it, preferring a non-trump card over spending
//   trump; only spend trump if the trick actually carries points/the jack
//   (or there's no non-trump option at all) - otherwise duck. Without this,
//   the heuristic will happily burn a scarce trump card (even the trump
//   jack itself) just to snatch a trick worth nothing, which randomizes
//   who ends up with the Jack/Low points regardless of hand strength.
const chooseHeuristicCard = (hand, trick, trickSuit, trump, mySeat, mustLeadTrump) => {
  const legal = legalMoves(hand, trickSuit, trump, mustLeadTrump);
  if (legal.length === 1) return legal[0];

  if (trick.length === 0) {
    const trumpAce = legal.find((c) => c.details.trump && c.details.value === 14);
    if (trumpAce) return trumpAce;
    return lowestOf(legal);
  }

  const winnerSeat = trickWinnerSeat(trick, trickSuit);
  if (seatTeam(winnerSeat) === seatTeam(mySeat)) {
    return lowestOf(legal);
  }

  const currentBest = trick.find((c) => c.details.owner === winnerSeat);
  const winners = legal.filter((c) => beats(c, currentBest, trickSuit));
  if (winners.length === 0) return lowestOf(legal);

  const nonTrumpWinners = winners.filter((c) => !c.details.trump);
  if (nonTrumpWinners.length > 0) return lowestOf(nonTrumpWinners);

  if (trickWorthTrumping(trick)) return lowestOf(winners);
  return lowestOf(legal);
};

export { chooseHeuristicCard };
